import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

// The planner's own blocking dialog (mock .overlay/.modal, B-34): never dismissible, focus kept inside. The shared
// Modal stays as it is in main for the other screens. A window without buttons (loading, rate-limited,
// recalculating) focuses the panel itself, so the focus never stays behind it (B2-11).
function PlannerDialog({ labelledBy, children }) {
  const panel = useRef(null);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  // After every render: a button the content autofocused wins; otherwise the panel takes the focus.
  useEffect(() => {
    if (!panel.current.contains(document.activeElement)) panel.current.focus();
  });

  const trapFocus = (e) => {
    if (e.key !== 'Tab') return;
    const items = [...panel.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return ReactDOM.createPortal(
    <div
      data-testid="planner-dialog-scrim"
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-[rgb(var(--scrim)/var(--scrim-alpha))] p-4">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className={clsx(
          'outline-none',
          'flex w-full max-w-[480px] flex-col gap-4 rounded-[20px] border border-border bg-surface p-7 shadow-pop',
          'animate-fade-in motion-reduce:animate-none',
        )}>
        {children}
      </div>
    </div>,
    document.querySelector('.modal-container') || document.body,
  );
}

export default PlannerDialog;
